import { useEffect, useRef, useState } from "react";

import { VideoFrame } from "@/types";

import {
  ensureVideoReady,
  extractFrameFromVideo,
} from "../../../../utils/video";

const FRAME_EXTRACTION_INTERVAL = 100; // Extract frame every 100ms

interface UseVideoLoaderProps {
  videoSrc: string;
  onDurationSet: (duration: number) => void;
  onFramesSet: (frames: VideoFrame[]) => void;
}

interface UseVideoLoaderReturn {
  videoRef: React.RefObject<HTMLVideoElement>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  isLoading: boolean;
  error: string | null;
}

export const useVideoLoader = (
  props: UseVideoLoaderProps,
): UseVideoLoaderReturn => {
  const { videoSrc, onDurationSet, onFramesSet } = props;

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Extract frames from video
  useEffect(() => {
    const extractFrames = async () => {
      if (!videoRef.current || !canvasRef.current) return;

      const video = videoRef.current;
      const canvas = canvasRef.current;

      try {
        // Wait for video metadata to load using shared utility
        await ensureVideoReady(video);

        onDurationSet(video.duration);
        const extractedFrames: VideoFrame[] = [];
        let frameIndex = 0;

        // Extract frames at intervals
        for (
          let time = 0;
          time < video.duration;
          time += FRAME_EXTRACTION_INTERVAL / 1000
        ) {
          const frameSrc = await extractFrameFromVideo(video, canvas, time);

          // Only add frame if extraction was successful
          if (frameSrc) {
            extractedFrames.push({
              timestamp: time,
              src: frameSrc,
              index: frameIndex,
            });
            frameIndex++;
          } else {
            console.error(`Failed to extract frame at time ${time}s`);
          }
        }

        if (extractedFrames.length === 0) {
          throw new Error(
            "No frames could be extracted from the video. This may be due to CORS restrictions.",
          );
        }

        onFramesSet(extractedFrames);
        setIsLoading(false);
      } catch {
        setError("Failed to load video.");
        setIsLoading(false);
      }
    };

    extractFrames();
  }, [videoSrc, onDurationSet, onFramesSet]);

  return {
    videoRef,
    canvasRef,
    isLoading,
    error,
  };
};
